import type {
  ChangeEvent,
  Dispatch,
  KeyboardEvent,
  SetStateAction,
} from 'react';

export function getAutocompleteTriggerRange(
  value: string,
  cursor: number,
  pattern: RegExp,
) {
  const match = pattern.exec(value.slice(0, cursor));

  return match
    ? {
        query: match[2] ?? '',
        start: cursor - match[0].length + (match[1]?.length ?? 0),
      }
    : null;
}

export function updateAutocompleteQuery(
  event: ChangeEvent<HTMLTextAreaElement>,
  getTriggerRange: (
    value: string,
    cursor: number,
  ) => { query: string; start: number } | null,
  setAnchorEl: Dispatch<SetStateAction<HTMLTextAreaElement | null>>,
  setQuery: Dispatch<SetStateAction<string | null>>,
) {
  setAnchorEl(event.currentTarget);
  const range = getTriggerRange(
    event.target.value,
    event.target.selectionStart,
  );
  setQuery(range?.query ?? null);
  if (!range) setAnchorEl(null);

  return range;
}

export function insertAutocompleteSuggestion({
  getTriggerRange,
  onChange,
  replacement,
  textarea,
}: {
  getTriggerRange: (
    value: string,
    cursor: number,
  ) => { query: string; start: number } | null;
  onChange: (value: string) => void;
  replacement: string;
  textarea: HTMLTextAreaElement | null;
}) {
  if (!textarea) return false;

  const cursor = textarea.selectionStart;
  const range = getTriggerRange(textarea.value, cursor);
  if (!range) return false;

  onChange(
    `${textarea.value.slice(0, range.start)}${replacement}${textarea.value.slice(cursor)}`,
  );
  requestAnimationFrame(() => {
    const nextCursor = range.start + replacement.length;
    textarea.focus();
    textarea.setSelectionRange(nextCursor, nextCursor);
  });
  return true;
}

export function handleAutocompleteNavigation<T>(
  event: KeyboardEvent<HTMLTextAreaElement>,
  suggestions: readonly T[],
  setSelectedIndex: Dispatch<SetStateAction<number>>,
) {
  if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return false;

  event.preventDefault();
  const direction = event.key === 'ArrowDown' ? 1 : -1;
  setSelectedIndex(
    (index) => (index + direction + suggestions.length) % suggestions.length,
  );
  return true;
}

export function handleAutocompleteSelection<T>(
  event: KeyboardEvent<HTMLTextAreaElement>,
  suggestions: readonly T[],
  selectedIndex: number,
  selectSuggestion: (suggestion: T) => void,
) {
  const isSelectionKey =
    (event.key === 'Enter' && !event.nativeEvent.isComposing) ||
    event.key === 'Tab';
  if (!isSelectionKey) return false;

  event.preventDefault();
  const suggestion = suggestions[selectedIndex];
  if (suggestion) selectSuggestion(suggestion);
  return true;
}

export function handleAutocompleteKeyDown<T>(
  event: KeyboardEvent<HTMLTextAreaElement>,
  isOpen: boolean,
  suggestions: readonly T[],
  selectedIndex: number,
  setSelectedIndex: Dispatch<SetStateAction<number>>,
  selectSuggestion: (suggestion: T) => void,
  close: () => void,
) {
  if (!isOpen || suggestions.length === 0) return;

  if (
    handleAutocompleteNavigation(event, suggestions, setSelectedIndex) ||
    handleAutocompleteSelection(
      event,
      suggestions,
      selectedIndex,
      selectSuggestion,
    )
  )
    return;

  if (event.key === 'Escape') {
    event.preventDefault();
    close();
  }
}
